import { useState } from 'react';
import { createRoute, Link, useRouter } from '@tanstack/react-router';
import { Route as rootRoute } from './__root.js';
import { Button } from '../components/ui/Button.js';
import { Input } from '../components/ui/Input.js';
import { Label } from '../components/ui/Label.js';
import { ApiError, loginRequest, loginTwoFactorRequest } from '../lib/api.js';
import { useAuthStore } from '../lib/auth.js';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

function LoginPage() {
  const router = useRouter();
  const setSession = useAuthStore((s) => s.setSession);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // When the password is correct but the account has 2FA, we hold the pending
  // token and switch the form to a code step.
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [code, setCode] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await loginRequest(email, password);
      if ('twoFactorRequired' in result) {
        setPendingToken(result.pendingToken);
        return;
      }
      setSession(result.accessToken, result.user);
      router.navigate({ to: '/app' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleTwoFactorSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!pendingToken) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await loginTwoFactorRequest(pendingToken, code.trim());
      setSession(result.accessToken, result.user);
      router.navigate({ to: '/app' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-cream">
      {/* Left: brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-navy text-cream relative overflow-hidden">
        {/* Decorative gold arc */}
        <div className="absolute -bottom-20 -left-20 h-96 w-96 rounded-full bg-gold/10 blur-3xl" />
        <div className="absolute top-1/3 -right-32 h-72 w-72 rounded-full bg-gold/15 blur-2xl" />

        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-cream flex items-center justify-center">
              <span className="font-serif text-navy text-xl leading-none">A</span>
            </div>
            <div className="font-serif text-2xl tracking-tighter2">Admitly</div>
          </div>
        </div>

        <div className="relative z-10 space-y-6 max-w-md">
          <h1 className="font-serif text-5xl leading-[1.05] tracking-tighter2 text-cream">
            The catalogue
            <br />
            for Australian
            <br />
            <span className="text-gold-soft italic">education agents.</span>
          </h1>
          <p className="text-cream/70 text-base leading-relaxed font-sans">
            A clean, current view of every Australian course — without the spreadsheets.
          </p>
        </div>

        <div className="relative z-10 text-xs text-cream/50 font-sans">
          © {new Date().getFullYear()} Admitly · Australia-only · v0.1
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-10 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-navy flex items-center justify-center">
              <span className="font-serif text-cream text-lg leading-none">A</span>
            </div>
            <div className="font-serif text-2xl tracking-tighter2 text-ink">Admitly</div>
          </div>

          <h2 className="font-serif text-3xl tracking-tighter2 mb-1.5">
            {pendingToken ? 'Two-factor' : 'Sign in'}
          </h2>
          <p className="text-sm text-muted mb-8">
            {pendingToken
              ? 'Enter the 6-digit code from your authenticator app (or a recovery code).'
              : 'Welcome back. Enter your credentials to continue.'}
          </p>

          {pendingToken ? (
            <form onSubmit={handleTwoFactorSubmit} className="space-y-4">
              <div>
                <Label htmlFor="code">Verification code</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                />
              </div>

              {error && (
                <div className="rounded-lg bg-danger-bg border border-danger/20 px-3 py-2 text-sm text-danger">
                  {error}
                </div>
              )}

              <Button type="submit" size="lg" loading={submitting} className="w-full mt-2">
                Verify
              </Button>
              <button
                type="button"
                className="w-full text-sm text-muted hover:text-ink"
                onClick={() => {
                  setPendingToken(null);
                  setCode('');
                  setError(null);
                }}
              >
                Back to sign in
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@agency.com.au"
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••"
                />
              </div>

              {error && (
                <div className="rounded-lg bg-danger-bg border border-danger/20 px-3 py-2 text-sm text-danger">
                  {error}
                </div>
              )}

              <Button type="submit" size="lg" loading={submitting} className="w-full mt-2">
                Sign in
              </Button>
            </form>
          )}

          {!pendingToken && (
            <div className="mt-8 text-xs text-muted space-y-1.5">
              <div>
                <Link to="/forgot-password" className="text-navy hover:underline">
                  Forgot your password?
                </Link>
              </div>
              <div>Trouble signing in? Contact your administrator.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
